import React, { useEffect, useState } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Paper,
  Box,
  useTheme,
  IconButton,
  Typography,
  Tooltip,
} from "@mui/material";
import {
  AssignmentReturn,
  CardMembership,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import certificationSchema from "./certificationSchema";
import { getCertificationFields } from "./certificationFields";
import { useCertificationData } from "./useCertificationData";
import PATCloseButton from "../../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../../components/Buttons/PATSaveButton";
import FormSection from "../../../components/Form/FormSection";
import {
  createTrainingCertification,
  updateTrainingCertification,
} from "../../../reducers/TrainingCertificationReducer";
import { PATH_TRAINING_CERTIFICATIONS } from "../../../utils/routeConstants";
import TagsTable from "../../../components/Tags/TagsTable";
import { PerTagType } from "../../../utils/enums";
import { getEmployeeOptionLabel, printAMSReport } from "../../../utils/functions";
import { areAllPermissionsDisabled, formatModulePermission, getAccessPermission, getModuleAccessPermission } from "../../../reducers/PermissionsReducer";
import { notify } from "../../../reducers/NotifierReducer";
import { getService } from "../../../reducers/service";
import { AMSReportID, entityNamesForCustomId, serviceNames } from "../../../utils/constants";
import PrintBase64Pdf from "../../../components/PrintBase64Pdf";
import PATPrintButton from "../../../components/Buttons/PATPrintButton";
import { formatSaveDate } from "../../../reducers/TimeReducer";

const AddEditTrainingCertification = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const theme = useTheme();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const readOnly = queryParams.get("readOnly") === "true";

  useEffect(() => {
    if (location.search && queryParams.get("readOnly") !== "true") {
      dispatch(notify("The requested page does not exist.", "warning"));
      history.push(PATH_TRAINING_CERTIFICATIONS);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location.search, history]);

  const [allExpanded, setAllExpanded] = useState(true);
  const [printReport, setPrintReport] = useState(false);
  const [reportString, setReportString] = useState("");

  const handleToggleAll = () => {
    setAllExpanded((prev) => !prev);
  };

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(certificationSchema),
    defaultValues: {
      id: null,
      certificationType: "",
      dateObtained: "",
      expireDate: "",
      employee: "",
      fromWhom: "",
      comments: "",
      certificateNumber: "",
    },
  });

  const { loaded, employees, certificationTypeOptions, tagTypeOptions } =
    useCertificationData(id, reset);
  const user = useSelector((state) => state.user);

  const personnelTags = useSelector((state) =>
    state.personnelTags.personnelTags &&
    state.personnelTags.personnelTags[PerTagType.Certification]
      ? state.personnelTags.personnelTags[PerTagType.Certification][id]
      : []
  );

  const trainingPermissions = getModuleAccessPermission("training", "any");
  const trainingModulePermissions = formatModulePermission(
    "TrainingModule",
    trainingPermissions,
    [
      "Courses",
      "Participants",
      "Certifications",
      "Summary",
      "Mass Update",
      "Course Information",
      "Course Participant",
      "Course Certificate Information",
      "Course Tags",
      "Participant Information",
      "Participant Tags",
      "Training Cost",
    ]
  );
  const isDisabledModule = areAllPermissionsDisabled(
    trainingModulePermissions["TrainingModule"]
  );

  const certificateInfoTabPermission = getAccessPermission(
    "training",
    "Certificate Information",
    "any"
  );

  const certificateInfoTabReadPermission =
    certificateInfoTabPermission?.Read ?? true;
  const certificateInfoTabEditPermission =
    certificateInfoTabPermission?.Edit ?? true;

  const certificateTagsTabPermission = getAccessPermission(
    "training",
    "Certificate Tags",
    "any"
  );

  const certificateTagsTabReadPermission =
    certificateTagsTabPermission?.Read ?? true;

  const options = {
    employee: employees.map((emp) => ({
      label: getEmployeeOptionLabel(emp),
      value: emp.employeeNum,
    })),
    certificationType: certificationTypeOptions.map((type) => ({
      label: type,
      value: type,
    })),
  };

  useEffect(() => {
    setValue("certificateNumber", "");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.userAgency]);

  const generateCustomId = async () => {
    const data = {
      EntityName: entityNamesForCustomId.CertificateId,
      agencyID: user.userAgency,
    };
    const service = getService(serviceNames.customId);
    service.timeout = 20000;

    const id = await service.create(data);
    if (id?.includes("Agency ID does not match")) {
      dispatch(
        notify(
          "Unable to generate Certificate ID because the Agency ID does not match.",
          "warning"
        )
      );
      return null;
    }
    return id;
  };

  const handleAutoGenerateClick = async () => {
    const id = await generateCustomId();
    setValue("certificateNumber", id);
  };

  const onSubmit = async (data) => {
    const certificateId = await generateCustomId();
    const cleanedData = {
      certificationType: data.certificationType.trim() || null,
      dateObtained: data.dateObtained
        ? formatSaveDate(data.dateObtained)
        : null,
      expireDate: data.expireDate
        ? formatSaveDate(data.expireDate)
        : null,
      certificateNumber: data.certificateNumber || certificateId,
      employee: data.employee || null,
      fromWhom: data.fromWhom || null,
      comments: data.comments.trim() || null,
      agencyId: user.userAgency,
    };

    if (id) {
      await dispatch(updateTrainingCertification(data.id, cleanedData));
    } else {
      await dispatch(createTrainingCertification(cleanedData));
    }

    history.push(`${PATH_TRAINING_CERTIFICATIONS}`);
  };

  if (!loaded) return null;

  const sectionProps = {
    control,
    errors,
    options,
    theme,
    watch,
  };

  const renderIdGenerationButton = () => {
    return (
      <Box>
        <Tooltip title="Auto generate certificate number">
          <IconButton
            color="primary"
            aria-label="Auto generate certificate number"
            size="large"
            onClick={handleAutoGenerateClick}
          >
            <AssignmentReturn />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const formSection = [
    certificateInfoTabReadPermission && {
      title: "Certification Information",
      icon: <CardMembership />,
      fields: getCertificationFields(),
      allExpanded,
      defaultExpanded: true,
      editPermission: id ? !readOnly && certificateInfoTabEditPermission : true,
      isEdit: id ? true : false,
      renderIdGenerationButton: renderIdGenerationButton,
    },
  ];

  const handlePrintCertificationProfile = async () => {
      const result = await printAMSReport(
        AMSReportID.Certifications,
        id,
        user.userAgency,
        trainingModulePermissions,
        user.userData.user.Username
      );
  
      if (result) {
        setReportString(result?.base64 || "");
        setPrintReport(true);
      }
    };

  return (
    loaded && (
      <Paper
        elevation={3}
        sx={{
          padding: theme.spacing(3),
          margin: theme.spacing(1),
          backgroundColor: theme.palette.background.paper,
          overflow: "hidden",
          height: "calc(100vh - 100px)",
          marginTop: theme.spacing(0.5),
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton
              onClick={handleToggleAll}
              title={allExpanded ? "Collapse All" : "Expand All"}
            >
              {allExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
            <Typography variant="h4" gutterBottom>
              {id ? "Edit Certification" : "Add Certification"}
            </Typography>
          </Box>
          <Box display="flex" gap={2}>
            {!isDisabledModule && (
            <>
              {id && (
                <PATPrintButton
                  tooltipText="Print Certification Profile"
                  color="primary"
                  onClick={handlePrintCertificationProfile}
                />
              )}
              {!readOnly && (
                <PATSaveButton
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit(onSubmit)}
                />
              )}
            </>
            )}
            <PATCloseButton
              tooltipText="Close"
              onClick={() => history.push(`${PATH_TRAINING_CERTIFICATIONS}`)}
            />
          </Box>
        </Box>

        <Box sx={{ height: "70vh", overflowY: "auto", pr: 1 }}>
          <form>
            {formSection.filter(Boolean).map((section) => (
              <FormSection key={section.title} {...section} {...sectionProps} />
            ))}
            {id ? (
              certificateTagsTabReadPermission && (
                <TagsTable
                  id={id}
                  tagTypeOptions={tagTypeOptions}
                  personnelTags={personnelTags}
                  theme={theme}
                  allExpanded={allExpanded}
                  perTagType={PerTagType.Certification}
                  readOnly={readOnly}
                />
              )
            ) : (
              <></>
            )}
          </form>
        </Box>
        <PrintBase64Pdf base64={reportString} open={printReport} />
      </Paper>
    )
  );
};

export default AddEditTrainingCertification;
